import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import styles from './Scrims.module.css';
import { getScrims, deleteScrim, getScrimTracks, launchScrim } from '../utils/api';
import RestartScrimProcessing from '../components/RestartScrimProcessing';

interface Scrim {
  scrim_id: string;
  name: string;
  date_utc: string;
  user_id: string;
  org_id?: string;
  // Additional details loaded separately
  tracks_count?: number;
  maps_count?: number;
  rounds_count?: number;
  processing_status?: string;
  processing_error?: string;
  details_loading?: boolean;
  details_error?: string;
}

const PAGE_SIZE = 8;

const Scrims: React.FC = () => {
  const [scrims, setScrims] = useState<Scrim[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [restartScrimId, setRestartScrimId] = useState<string | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const navigate = useNavigate();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadScrims();
    
    // Cleanup function to clear interval when component unmounts
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Set up polling for scrims that are not completed or in error state
  useEffect(() => {
    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Check if there are any scrims that need polling
    const needsPolling = scrims.some(scrim => {
      const status = scrim.processing_status;
      return status && status !== 'COMPLETED' && status !== 'ERROR';
    });

    if (needsPolling) {
      // Set up polling every 10 seconds
      pollingIntervalRef.current = setInterval(() => {
        const scrimsToUpdate = scrims.filter(scrim => {
          const status = scrim.processing_status;
          return status && status !== 'COMPLETED' && status !== 'ERROR';
        });
        
        if (scrimsToUpdate.length > 0) {
          loadScrimDetails(scrimsToUpdate);
        }
      }, 10000);
    }

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [scrims]);

  const loadScrims = async () => {
    try {
      setIsLoading(true);
      const response = await getScrims();
      const scrims = response.scrims || [];

      // Sort by date (most recent first), then by name
      const sortedScrims = scrims.sort((a: Scrim, b: Scrim) => {
        // First sort by date (descending - most recent first)
        const dateCompare = new Date(b.date_utc).getTime() - new Date(a.date_utc).getTime();
        if (dateCompare !== 0) {
          return dateCompare;
        }
        
        // If dates are equal, sort by name (ascending)
        return a.name.localeCompare(b.name);
      });

      setScrims(sortedScrims);
      setVisibleCount(PAGE_SIZE);

      // Load details only for the initially visible scrims
      const initialScrimsToLoad = sortedScrims.slice(0, PAGE_SIZE);
      if (initialScrimsToLoad.length > 0) {
        loadScrimDetails(initialScrimsToLoad);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scrims');
    } finally {
      setIsLoading(false);
    }
  };

  const loadScrimDetails = async (scrimsToUpdate: Scrim[]) => {
    // Load details for each scrim in parallel
    const detailPromises = scrimsToUpdate.map(async (scrim) => {
      try {
        // Only set loading state if this is the initial load (not polling)
        const isInitialLoad = scrim.details_loading === undefined;
        if (isInitialLoad) {
          setScrims(prev => prev.map(s => 
            s.scrim_id === scrim.scrim_id 
              ? { ...s, details_loading: true, details_error: undefined }
              : s
          ));
        }

        const response = await getScrimTracks(scrim.scrim_id);
        const tracks = response.tracks || [];
        
        // Calculate statistics
        const tracks_count = tracks.length;
        const unique_maps = new Set<number>();
        let total_rounds = 0;
        
        tracks.forEach((track: any) => {
          const rounds = track.rounds || [];
          total_rounds += rounds.length;
          rounds.forEach((round: any) => {
            if (round.map_index !== undefined) {
              unique_maps.add(round.map_index);
            }
          });
        });
        
        const maps_count = unique_maps.size;
        const rounds_count = total_rounds;

        // Extract processing status from the first track (track_index = 1)
        const processing_status = tracks.find((track: any) => track.track_index === 1)?.processing_status;
        const processing_error = tracks.find((track: any) => track.track_index === 1)?.processing_error;

        // Update scrim with details
        setScrims(prev => prev.map(s => 
          s.scrim_id === scrim.scrim_id 
            ? { 
                ...s, 
                tracks_count, 
                maps_count, 
                rounds_count,
                processing_status,
                processing_error,
                details_loading: false,
                details_error: undefined
              }
            : s
        ));
      } catch (err) {
        // Set error state
        setScrims(prev => prev.map(s => 
          s.scrim_id === scrim.scrim_id 
            ? { 
                ...s, 
                details_loading: false, 
                details_error: err instanceof Error ? err.message : 'Failed to load details'
              }
            : s
        ));
      }
    });

    // Wait for all details to load (but don't block the UI)
    await Promise.allSettled(detailPromises);
  };

  const handleCreateNew = () => {
    navigate('/create-scrim');
  };

  const handleDelete = async (scrimId: string) => {
    try {
      setIsDeleting(true);
      await deleteScrim(scrimId);
      await loadScrims(); // Refresh the list
      setDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete scrim');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestart = async (scrimId: string) => {
    try {
      setIsRestarting(true);
      await launchScrim(scrimId);
      await loadScrims(); // Refresh the list
      setRestartScrimId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restart processing');
    } finally {
      setIsRestarting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toISOString().split('T')[0];
  };

  const formatProcessingStatus = (status?: string, error?: string) => {
    if (!status) {
      return { text: 'QUEUING', className: styles.statusNotStarted, showSpinner: true };
    }
    
    switch (status) {
      case 'QUEUED':
        return { text: 'QUEUED', className: styles.statusQueued, showSpinner: false };
      case 'LAUNCHED':
        return { text: 'LAUNCHING', className: styles.statusLaunching, showSpinner: true };
      case 'INITIALIZING':
        return { text: 'INITIALIZING', className: styles.statusInitializing, showSpinner: true };
      case 'CONNECTING':
        return { text: 'CONNECTING', className: styles.statusConnecting, showSpinner: true };
      case 'PROCESSING':
        return { text: 'PROCESSING', className: styles.statusProcessing, showSpinner: true };
      case 'COMPLETED':
        return { text: '✓ COMPLETED', className: styles.statusCompleted, showSpinner: false };
      case 'ERROR':
        return { text: 'ERROR', className: styles.statusError, error, showSpinner: false };
      default:
        return { text: status.toUpperCase(), className: styles.statusUnknown, showSpinner: false };
    }
  };

  const handleShowMore = () => {
    const nextVisibleCount = Math.min(visibleCount + PAGE_SIZE, scrims.length);
    if (nextVisibleCount > visibleCount) {
      const newlyVisibleScrims = scrims
        .slice(visibleCount, nextVisibleCount)
        .filter(scrim => scrim.tracks_count === undefined && !scrim.details_error);

      if (newlyVisibleScrims.length > 0) {
        loadScrimDetails(newlyVisibleScrims);
      }
    }
    setVisibleCount(nextVisibleCount);
  };

  const visibleScrims = scrims.slice(0, visibleCount);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Error: {error}
          <button onClick={loadScrims} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>
          <i className="bi bi-camera-video"></i>
          Scrims
        </h1>
        <p>Manage your team practice sessions</p>
      </div>

      {scrims.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <i className="bi bi-camera-video"></i>
          </div>
          <h3>Create your first scrim</h3>
          <p>
            Have each player stream their POV to YouTube.<br />
            Automatically sync all the videos together as a Valolens scrim.</p>
          <button 
            className={styles.createButton}
            onClick={handleCreateNew}
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create new scrim'}
          </button>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.columnDate}>Date</th>
                  <th className={styles.columnName}>Name</th>
                  <th className={styles.columnTracks}>Tracks</th>
                  <th className={styles.columnMaps}>Maps</th>
                  <th className={styles.columnRounds}>Rounds</th>
                  <th className={styles.columnProcessing}>Processing</th>
                  <th className={styles.columnActions}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleScrims.map((scrim) => (
                  <tr key={scrim.scrim_id} className={styles.tableRow}>
                    <td className={styles.columnDate}>
                      {formatDate(scrim.date_utc)}
                    </td>
                    <td className={styles.columnName}>
                      <div className={styles.nameCell}>
                        <span className={styles.scrimName}>{scrim.name}</span>
                      </div>
                    </td>
                    <td className={styles.columnTracks}>
                      {scrim.details_loading ? (
                        <div className={styles.statLoading}>
                          <div className={styles.miniSpinner}></div>
                        </div>
                      ) : scrim.details_error ? (
                        <div className={styles.statError}>
                          <i className="bi bi-exclamation-triangle"></i>
                        </div>
                      ) : (
                        <span className={styles.statValue}>{scrim.tracks_count || 0}</span>
                      )}
                    </td>
                    <td className={styles.columnMaps}>
                      {scrim.details_loading ? (
                        <div className={styles.statLoading}>
                          <div className={styles.miniSpinner}></div>
                        </div>
                      ) : scrim.details_error ? (
                        <div className={styles.statError}>
                          <i className="bi bi-exclamation-triangle"></i>
                        </div>
                      ) : (
                        <span className={styles.statValue}>{scrim.maps_count || 0}</span>
                      )}
                    </td>
                    <td className={styles.columnRounds}>
                      {scrim.details_loading ? (
                        <div className={styles.statLoading}>
                          <div className={styles.miniSpinner}></div>
                        </div>
                      ) : scrim.details_error ? (
                        <div className={styles.statError}>
                          <i className="bi bi-exclamation-triangle"></i>
                        </div>
                      ) : (
                        <span className={styles.statValue}>{scrim.rounds_count || 0}</span>
                      )}
                    </td>
                    <td className={styles.columnProcessing}>
                      {scrim.details_loading ? (
                        <div className={styles.statLoading}>
                          <div className={styles.miniSpinner}></div>
                        </div>
                      ) : scrim.details_error ? (
                        <div className={styles.statError}>
                          <i className="bi bi-exclamation-triangle"></i>
                        </div>
                      ) : (
                        (() => {
                          const statusInfo = formatProcessingStatus(scrim.processing_status, scrim.processing_error);
                          return (
                            <div className={styles.processingStatusContainer}>
                              <div 
                                className={`${styles.statValue} ${statusInfo.className}`}
                                style={{ display: 'inline-flex', alignItems: 'center' }}
                                title={statusInfo.error || undefined}
                              >
                                {statusInfo.showSpinner && (
                                  <Spinner
                                    animation="grow"
                                    size="sm"
                                    style={{ 
                                      color: 'currentColor',
                                      marginRight: '0.5rem',
                                      position: 'relative',
                                      width: '0.8rem',
                                      height: '0.8rem',
                                      top: '-0.05rem'
                                    }}
                                  />
                                )}
                                {statusInfo.text}
                              </div>
                              {scrim.processing_status === 'ERROR' && (
                                <button
                                  className={styles.restartButton}
                                  onClick={() => setRestartScrimId(scrim.scrim_id)}
                                  title="Restart processing"
                                >
                                  Restart
                                </button>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </td>
                    <td className={styles.columnActions}>
                      <div className={styles.actionButtons}>
                        <a 
                          href={`/scrim/${scrim.scrim_id}/replay`} 
                          className={styles.replayButton}
                        >
                          <i className="bi bi-play"></i>
                          Replay
                        </a>
                        {/* <Link 
                          to={`/scrim/${scrim.scrim_id}`} 
                          className={styles.tracksButton}
                        >
                          <i className="bi bi-list-ul"></i>
                          Tracks
                        </Link> */}
                        <button 
                          className={styles.removeButton}
                          onClick={() => setDeleteConfirm(scrim.scrim_id)}
                        >
                          <i className="bi bi-trash"></i>
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {visibleCount < scrims.length && (
                  <tr className={styles.showMoreRow}>
                    <td colSpan={7} className={styles.showMoreCell}>
                      <button
                        type="button"
                        className={styles.showMoreButton}
                        onClick={handleShowMore}
                      >
                        Show more
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.createNewContainer}>
            <button 
              className={styles.createButton}
              onClick={handleCreateNew}
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create new scrim'}
            </button>
          </div>
        </>
      )}

      {deleteConfirm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Delete Scrim</h3>
            <p>
              Are you sure you want to delete this scrim? This action cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <button 
                className={styles.cancelButton}
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className={styles.deleteButton}
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
              >
                {isDeleting && <div className={styles.buttonSpinner}></div>}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {restartScrimId && (
        <RestartScrimProcessing
          isOpen={true}
          onClose={() => setRestartScrimId(null)}
          onRestart={() => handleRestart(restartScrimId)}
          isRestarting={isRestarting}
        />
      )}
    </div>
  );
};

export default Scrims; 